import { FC, useMemo } from "react";

import { useRouter } from "next/router";

import useLayoutStore from "@/store/useLayoutStore";

import { useTokenData } from "@/hooks/useTokenData";

import { Layout, Menu } from "antd";

import {
  // HiHome,
  HiUsers,
  HiUserGroup,
  HiCalendar,
  HiClipboard,
  HiClipboardList,
  HiBadgeCheck,
  // HiMailOpen,
  HiOutlinePresentationChartLine,
  HiOutlineChartPie,
  HiOutlineFlag,
  HiOutlinePuzzle,
  HiOutlineCog,
  HiHome,
} from "react-icons/hi";

import BrandLogo from "./BrandLogo";
import { PAGE_URL } from "@/utils/constants";
import Link from "next/link";
import useWindowResize, { useWindowResizeTablet } from "@/hooks/useWindowResize";
import useDashboardName from "@/hooks/useDashboardName";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useIsViewAsHR } from "@/hooks/useIsViewAsHR";

const { Sider } = Layout;

const Sidebar: FC = () => {  
  const { track } = useAnalytics();
  
  const collapsed = useLayoutStore.useCollapsed();

  const router = useRouter();
  const { isHR } = useIsViewAsHR();

  const tokenData = useTokenData();

  const pathName = router.pathname.split("/");

  const pathKeys: string | string[] = useMemo(() => {
    if (router.query?.view === "admin" && router.query?.fr) {
      return router.query?.fr.includes("_")
        ? router.query?.fr.toString().split("_")
        : [`menu-${router.query?.fr}`];
    } else {
      if (pathName[1] === "admin") {
        if (pathName[2]) {
          if (pathName[2] === "reports")
            return ["menu-reports", `menu-${pathName[3]}`];
          return [`menu-${pathName[2]}`];
        }

        return [`menu-${pathName[1]}`];
      }
      return pathName[1];
    }
  }, [pathName]);

  const isMobile = useWindowResize();
  const isTablet = useWindowResizeTablet();
  const dashboardName = useDashboardName();

  if(isMobile || isTablet) return null;

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={230}
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
      }}
    >
      <div className="flex flex-col justify-between h-full pb-4">
        <div>
          <BrandLogo className="my-6" />

          <Menu
            // theme="light"
            mode="inline"
            selectedKeys={
              pathKeys === "" ? ["menu-home"] : [`menu-${pathKeys}`]
            }
            items={[
              {
                key: "menu-dashboard",
                icon: <HiHome />,
                label: <p className="flex items-center gap-2">My Dashboard</p>,
                onClick: () => {
                  track("Left NavBar", "Click", `My Dashboard Menu`)
                  router.push(PAGE_URL.dashboard)
                },
              },
              {
                key: "menu-connections",
                icon: <HiUsers />,
                label: (
                  <span id={!isMobile ? "sidebar-my-connections" : ""}>
                    My Connections
                  </span>
                ),
                onClick: () => {
                  track("Left NavBar", "Click", `My Connections Menu`)
                  router.push(PAGE_URL.connections)
                },
              },
              // {
              //   key: "menu-notification",
              //   icon: <HiClipboardList />,
              //   label: "My Notifications",
              //   onClick: () => router.push(PAGE_URL.notification),
              // },
              // {
              //   key: "menu-circles",
              //   icon: <HiUserGroup />,
              //   label: "My Circles",
              //   onClick: () => router.push(PAGE_URL.circles),
              // },
              // {
              //   key: "menu-events",
              //   icon: <HiCalendar />,
              //   label: "My Events",
              //   onClick: () => router.push(PAGE_URL.events),
              // },
              {
                key: "menu-mycalendar",
                icon: <HiCalendar />,
                label: "My Community",
                onClick: () => {
                  track("Left NavBar", "Click", `My Community Menu`)
                  router.push(PAGE_URL.mycalendar)
                },
              },
              {
                key: "menu-goals",
                icon: <HiClipboard />,
                label: "My Goals",
                onClick: () => {
                  track("Left NavBar", "Click", `My Goals Menu`)
                  router.push(PAGE_URL.goals)
                },
              },
              {
                key: "menu-rewards",
                icon: <HiBadgeCheck />,
                label: "My Rewards",
                onClick: () => {
                  track("Left NavBar", "Click", `My Rewards Menu`)
                  router.push(PAGE_URL.rewards)
                },
              },
              // {
              //   key: "menu-inbox",
              //   icon: <HiMailOpen />,
              //   label: "My Inbox",
              //   onClick: () => router.push(PAGE_URL.inbox),
              // },
            ]}
          />

          <div className="mt-4 pt-2 border-t border-gray-600">
            <Menu
              // theme="light"
              mode="inline"
              selectedKeys={
                pathKeys === "" ? ["menu-home"] : [`menu-${pathKeys}`]
              }
              items={[
                {
                  key: "menu-how-to-use-myconnect",
                  label: <p>How to use MyConnect.ai</p>,
                  children: [
                    {
                      key: "menu-how-to-use-myconnect-global-search",
                      label: (
                        <Link
                          href={{
                            pathname: router.pathname,
                            query: {
                              ...router.query,
                              userguidevideo: "global-search",
                            },
                          }}
                          shallow
                          onClick={() => track("Left NavBar", "Click", `Global Search`)}
                        >
                          <p>Global Search</p>
                        </Link>
                      ),
                    },
                    {
                      key: "menu-how-to-use-myconnect-invite-champions",
                      label: (
                        <Link
                          href={{
                            pathname: router.pathname,
                            query: {
                              ...router.query,
                              userguidevideo: "invite-champions",
                            },
                          }}
                          shallow
                          onClick={() => track("Left NavBar", "Click", `Invite Champions`)}
                        >
                          <p>Invite Champions</p>
                        </Link>
                      ),
                    },
                    {
                      key: "menu-how-to-use-myconnect-make-connections",
                      label: (
                        <Link
                          href={{
                            pathname: router.pathname,
                            query: {
                              ...router.query,
                              userguidevideo: "make-connections",
                            },
                          }}
                          shallow
                          onClick={() => track("Left NavBar", "Click", `Make Connections`)}
                        >
                          <p>Make Connections</p>
                        </Link>
                      ),
                    },
                    ...(tokenData?.isPremium === "true"
                      ? [
                          {
                            key: "menu-how-to-use-myconnect-manage-your-goals",
                            label: (
                              <Link
                                href={{
                                  pathname: router.pathname,
                                  query: {
                                    ...router.query,
                                    userguidevideo: "manage-your-goals",
                                  },
                                }}
                                shallow
                                onClick={() => track("Left NavBar", "Click", `Manage your Goals`)}
                              >
                                <p>Manage your Goals</p>
                              </Link>
                            ),
                          },
                        ]
                      : []),
                    {
                      key: "menu-how-to-use-myconnect-complete-profile",
                      label: (
                        <Link
                          href={{
                            pathname: router.pathname,
                            query: {
                              ...router.query,
                              userguidevideo: "complete-profile",
                            },
                          }}
                          shallow
                          onClick={() => track("Left NavBar", "Click", `Complete your Profile`)}
                        >
                          <p>Complete your Profile</p>
                        </Link>
                      ),
                    },
                    {
                      key: "menu-how-to-use-myconnect-create-mentorship",
                      label: (
                        <Link
                          href={{
                            pathname: router.pathname,
                            query: {
                              ...router.query,
                              userguidevideo: "create-mentorship",
                            },
                          }}
                          shallow
                          onClick={() => track("Left NavBar", "Click", `Create Mentorships`)}
                        >
                          <p>Create Mentorships</p>
                        </Link>
                      ),
                    },
                    {
                      key: "menu-how-to-use-myconnect-set-goals",
                      label: (
                        <Link
                          href={{
                            pathname: router.pathname,
                            query: {
                              ...router.query,
                              userguidevideo: "set-goals",
                            },
                          }}
                          shallow
                          onClick={() => track("Left NavBar", "Click", `Set Goals`)}
                        >
                          <p>Set Goals</p>
                        </Link>
                      ),
                    },
                    ...(tokenData?.isPremium === "true"
                      ? [
                          {
                            key: "menu-how-to-use-myconnect-focused-milestone",
                            label: (
                              <Link
                                href={{
                                  pathname: router.pathname,
                                  query: {
                                    ...router.query,
                                    userguidevideo: "focused-milestone",
                                  },
                                }}
                                shallow
                                onClick={() => track("Left NavBar", "Click", `Focused Milestone`)}
                              >
                                <p>Focused Milestone</p>
                              </Link>
                            ),
                          },
                        ]
                      : []),
                  ],
                },
              ]}
            />
          </div>

          {isHR && (
            <div className="mt-2 pt-4 border-t border-gray-600">
              <Menu
                // theme="dark"
                mode="inline"
                selectedKeys={pathKeys as string[]}
                items={[
                  {
                    key: "menu-hr",
                    icon: <HiOutlinePresentationChartLine />,
                    label: dashboardName,
                    onClick: () => {
                      track("Left NavBar", "Click", `Insight Dashboard`)
                      router.push(PAGE_URL.hr)
                    },
                  },
                  {
                    key: "menu-reports",
                    icon: <HiOutlineChartPie />,
                    label: "Reports",

                    children: [
                      {
                        key: "menu-mentorship",
                        label: "Mentorship",
                        onClick: () => {
                          track("Left NavBar", "Click", "Mentorship")
                          router.push(PAGE_URL.reportsMentorship)
                        },
                      },
                      {
                        key: "menu-connections",
                        label: "Connections",
                        onClick: () => {
                          track("Left NavBar", "Click", "Connections")
                          router.push(PAGE_URL.reportsConnections)
                        },
                      },
                      // {/* remove this item for non premium users */}
                      ...(tokenData?.isPremium === "true"
                        ? [
                            {
                              key: "menu-milestones",
                              label: "Milestones",
                              onClick: () => {
                                  track("Left NavBar", "Click", "Milestones")
                                  router.push(PAGE_URL.reportsMilestones)
                              },
                            },
                          ]
                        : []),
                        {
                          key: "menu-report-rewards",
                          label: "Rewards",
                          onClick: () => {
                            track("Left NavBar", "Click", "Rewards")
                            router.push(PAGE_URL.reportsRewards)
                          },
                        }
                    ],
                  },
                  {
                    key: "menu-org-settings",
                    icon: <HiOutlineCog />,
                    label: "Organization Settings",
                    onClick: () => {
                      track("Left NavBar", "Click", "Organization Settings")
                      router.push(PAGE_URL.orgSettings)
                    },
                  },
                  {
                    key: "menu-manage-content",
                    icon: <HiOutlinePuzzle />,
                    label: "Manage Content",
                    onClick: () => {
                      track("Left NavBar", "Click", "Manage Content")
                      router.push(PAGE_URL.manageContent)
                    },
                  },
                  {
                    key: "menu-red-flag",
                    icon: <HiOutlineFlag />,
                    label: "Red Flags Inbox",
                    onClick: () => {
                      track("Left NavBar", "Click", "Red Flags Inbox")
                      router.push(PAGE_URL.redFlag)
                    },
                  },
                  // {
                  //   key: "menu-red-flag",
                  //   icon: <HiOutlineFlag />,
                  //   label: "Sentry Error",
                  //   onClick: () => router.push(PAGE_URL.sentry_test),
                  // },
                ]}
              />
            </div>
          )}
        </div>
      </div>
    </Sider>
  );
};

export default Sidebar;
